import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ConfirmModalComponent } from '~shared/components/confirm-modal/confirm-modal.component';
import { ModalService } from '~shared/services/modal/modal.service';
import { UnsavedChangesService } from '~shared/services/unsaved-changes/unsaved-changes.service';

/** Id de la modale, unique dans l'application (ModalService l'exige). */
export const UNSAVED_CHANGES_MODAL_ID = 'unsaved-changes-confirm';

/**
 * Modale "modifications non enregistrées", montée une seule fois dans
 * AppComponent.
 *
 * Elle est volontairement hors de toute autre modale : `ModalService` ne gère
 * qu'une modale ouverte à la fois (`close()` ferme "celle qui est ouverte"),
 * donc l'imbriquer dans la modale de vue de ContentComponent ne fonctionnerait
 * pas. Étant montée à la racine, elle est aussi disponible depuis n'importe
 * quelle route, y compris pendant une navigation annulée par le guard.
 */
@Component({
  selector: 'app-unsaved-changes-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule, ConfirmModalComponent],
  template: `
    <app-confirm-modal
      [id]="modalId"
      title="MODAL.UNSAVED_CHANGES.TITLE"
      cancelLabel="MODAL.UNSAVED_CHANGES.STAY"
      confirmLabel="MODAL.UNSAVED_CHANGES.LEAVE"
      [danger]="true"
      (confirmAct)="onConfirm()"
      (cancelAct)="onCancel()"
    >
      <p class="warning fs16">
        {{ 'MODAL.UNSAVED_CHANGES.DESCRIPTION' | translate }}
      </p>
      <div class="hub-checkbox">
        <input
          class="hub-checkbox__input"
          type="checkbox"
          id="unsaved-changes-skip"
          [checked]="skipFuture()"
          (change)="onSkipToggle($event)"
          data-test="unsaved-changes-skip-checkbox"
        />
        <label class="hub-checkbox__label" for="unsaved-changes-skip">
          {{ 'MODAL.UNSAVED_CHANGES.DONT_WARN_AGAIN' | translate }}
        </label>
      </div>
    </app-confirm-modal>
  `,
})
export class UnsavedChangesModalComponent {
  private readonly unsavedChanges = inject(UnsavedChangesService);
  private readonly modalService = inject(ModalService);

  protected readonly modalId = UNSAVED_CHANGES_MODAL_ID;
  protected readonly skipFuture = signal(false);

  constructor() {
    effect(() => {
      if (this.unsavedChanges.promptVisible()) {
        // Repartir décoché : le choix précédent n'a pas à être pré-coché, et
        // une fois la préférence posée la modale ne s'ouvre plus du tout.
        this.skipFuture.set(false);
        this.modalService.open(this.modalId);
      } else {
        this.modalService.close();
      }
    });
  }

  protected onSkipToggle(event: Event): void {
    this.skipFuture.set((event.target as HTMLInputElement).checked);
  }

  protected onConfirm(): void {
    this.unsavedChanges.acceptDiscard(this.skipFuture());
  }

  /**
   * Annulation (bouton "Rester", clic sur le fond). La case cochée n'est
   * volontairement PAS honorée ici : désactiver durablement l'avertissement
   * sur un geste d'annulation serait un effet de bord non voulu.
   */
  protected onCancel(): void {
    this.unsavedChanges.rejectDiscard();
  }
}
